import { Card, CardContent } from "@/components/ui/card";
import {
  EditBase,
  Form,
  useDataProvider,
  useEditContext,
  useNotify,
  type MutationMode,
} from "ra-core";

import {
  assertContactIsUnique,
  DuplicateRecordError,
} from "@/lib/duplicateIdentity";
import type { Contact } from "../types";
import { ContactAside } from "./ContactAside";
import { ContactInputs } from "./ContactInputs";
import { FormToolbar } from "../layout/FormToolbar";
import {
  cleanupContactForEdit,
  defaultEmailJsonb,
  defaultPhoneJsonb,
} from "./contactModel";

export const ContactEdit = ({
  mutationMode,
}: {
  mutationMode?: MutationMode;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  return (
    <EditBase
      redirect="show"
      transform={async (values) => {
        const cleaned = cleanupContactForEdit(values);
        try {
          await assertContactIsUnique(dataProvider, cleaned);
        } catch (error) {
          if (error instanceof DuplicateRecordError) {
            notify(error.message, { type: "warning" });
          }
          throw error;
        }
        return cleaned;
      }}
      mutationMode={mutationMode}
    >
      <ContactEditContent />
    </EditBase>
  );
};

const normalizeContactArrayFields = (record: Contact) => ({
  ...record,
  email_jsonb:
    record.email_jsonb && record.email_jsonb.length > 0
      ? record.email_jsonb
      : defaultEmailJsonb,
  phone_jsonb:
    record.phone_jsonb && record.phone_jsonb.length > 0
      ? record.phone_jsonb
      : defaultPhoneJsonb,
});

const ContactEditContent = () => {
  const { isPending, record } = useEditContext<Contact>();
  if (isPending || !record) return null;
  return (
    <div className="mt-2 flex gap-8">
      <Form
        className="flex flex-1 flex-col gap-4"
        record={normalizeContactArrayFields(record)}
      >
        <Card>
          <CardContent>
            <ContactInputs />
            <FormToolbar />
          </CardContent>
        </Card>
      </Form>

      <ContactAside link="show" />
    </div>
  );
};
